import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, DestroyRef, inject, OnDestroy, OnInit, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { ConfirmDialogComponent } from '@app/components/_common/confirm-dialog/confirm-dialog.component';
import { tableAnimation } from '@app/models/animination-table';
import { AssmatFormation, AssmatFormationProgramme, AssmatProgramme, DisplayedAssmatFormation } from '@app/models/ass-mat';
import { PermissionService, PlatformService, SnackbarService, UserService } from '@app/services';
import { AssmatService } from '@app/services/assmat.service';
import { distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-assmat-formation',
  templateUrl: './assmat-formation.component.html',
  styleUrl: './assmat-formation.component.scss',
  animations: [tableAnimation,
    trigger('detailExpand', [
      state('collapsed, void', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      transition('expanded <=> void', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ],
  standalone: false
})
export class AssmatFormationComponent implements OnInit, OnDestroy {

  assmatService: AssmatService = inject(AssmatService);
  userService: UserService = inject(UserService)
  platformService: PlatformService = inject(PlatformService);
  destroyRef: DestroyRef = inject(DestroyRef);
  permService: PermissionService = inject(PermissionService);
  snackbar: SnackbarService = inject(SnackbarService);
  dialog: MatDialog = inject(MatDialog);

  sort = viewChild<MatSort>(MatSort);
  paginator = viewChild<MatPaginator>(MatPaginator);
  table = viewChild<MatTable<DisplayedAssmatFormation>>('table');

  isMobile: boolean;
  deleting: number;
  formTitle: string;
  displayedColumns: string[];
  dataSource: MatTableDataSource<any>;
  loading = true;
  expandedFormation: AssmatFormation | null;
  saving: boolean = false;
  readOnly: boolean;
  pageTitle: string;
  listFormation: AssmatFormation[];
  listProgramme: AssmatProgramme[];
  editorToolbar = `bold italic underline | fontsizeselect | forecolor backcolor |
    alignleft aligncenter alignright `;


  ngOnInit(): void {

    this.platformService.isMobile$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged())
      .subscribe((isMobile) => {
        this.isMobile = isMobile;
        this.displayedColumns = isMobile ?
          ['dateDebut', 'dateFin', 'programme', 'action'] :
          ['dateDebut', 'dateFin', 'programme', 'lieu', 'action'];

      });

    this.readOnly = !this.permService.hasPermission('formations_edit') || !this.permService.hasPermission('informations_assmat_edit');

    this.assmatService.getFormations(this.userService.currentUser.idAssmat)
      .subscribe({
        next: (formationsProgrammes: AssmatFormationProgramme) => {
          // Formations + programmes
          this.listFormation = formationsProgrammes.formations;
          this.listProgramme = formationsProgrammes.programmes;

          // Table
          this.dataSource = new MatTableDataSource(this.listFormation || []);
          this.dataSource.sort = this.sort();
          this.dataSource.paginator = this.paginator();
        },
        error: () => this.snackbar.error("Erreur lors du chargement des données"),
        complete: () => this.loading = false
      })

  }

  isExpanded(formation: AssmatFormation) {
    return this.expandedFormation === formation;
  }

  toggle(formation: AssmatFormation) {
    this.expandedFormation = this.isExpanded(formation) ? null : formation;
  }

  addFormation() {
    this.updateTable(this.buildNewFormation());
  }

  deleteFormation(formation: DisplayedAssmatFormation) {
    const ref = this.dialog.open(ConfirmDialogComponent, { data: { message: `Supprimer cette formation ?` } });

    ref.afterClosed().subscribe(result => {
      if (result) {
        this.deleting = formation.idFormation;
        formation.rowEnterLeaveAnimation = 'deleted';
        const i = this.listFormation.findIndex(val => val.idFormation === formation.idFormation)

        setTimeout(() => {
          this.listFormation.splice(i, 1);
          this.refreshTable();
        }, 1);

        this.deleting = null;
      }
    });
  }

  buildNewFormation() {
    let listIds = this.listFormation.map(item => item.idFormation);
    let minId = Math.min(...listIds);

    return {
      dateDebut: '',
      dateFin: '',
      idFormation: minId < 0 ? minId - 1 : -1,
      idProgramme: this.listProgramme[0].idProgramme,
      programme: this.listProgramme[0].programme,
      lieu: '',
      rue: '',
      rue2: '',
      rue3: '',
      codePostal: '',
      ville: '',
      commentaire: ''
    } as DisplayedAssmatFormation;
  }

  hasInvalidDates(): boolean {
    return this.listFormation.some(f =>
      !f.dateDebut ||
      !f.dateFin ||
      new Date(f.dateFin) < new Date(f.dateDebut)
    );
  }


  refreshTable() {
    this.dataSource.data = this.listFormation;
    this.table().renderRows();
  }

  updateTable(newRow: DisplayedAssmatFormation) {
    newRow.rowEnterLeaveAnimation = 'new';
    newRow.highlightRowsAnimation = 'highlight';
    this.listFormation.push(newRow);
    setTimeout(() => {
      newRow.rowEnterLeaveAnimation = null;
      newRow.highlightRowsAnimation = null;
    }, 1000);

    setTimeout(() => {
      this.refreshTable();
    }, 1);
  }

  compareWithFn(programme1: AssmatProgramme, programme2: AssmatProgramme) {
    return programme1 && programme2 ?
      programme1.idProgramme === programme2.idProgramme :
      programme1 === programme2;
  }

  save() {

    if (this.readOnly || this.hasInvalidDates()) {
      return;
    }

    this.saving = true;

    this.assmatService.saveFormtaions(this.userService.currentUser.idAssmat, this.listFormation).subscribe(_ => {
      this.snackbar.info('Modification effectuée');
      this.saving = false;
      this.assmatService.clearFormationsCache();
      this.onBack();
    }, err => this.snackbar.error('Erreur lors de la modification'));
  }

  onBack() {
    history.back();
  }

  ngOnDestroy(): void {
    this.assmatService.clearFormationsCache();
  }
}
